import { Link } from 'react-router-dom';
import type { AnimeItem } from '@/mocks/anime';
import { useFavorites } from '@/hooks/useFavorites';

interface AnimeCardProps {
  anime: AnimeItem;
  rank?: number;
}

export default function AnimeCard({ anime, rank }: AnimeCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorited = isFavorite(anime.id);

  const handleFavorite = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    toggleFavorite(anime.id);
  };

  return (
    <Link
      to={`/detail/${anime.id}`}
      className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
    >
      <div className="flex sm:block">
        <div className="relative w-28 sm:w-full shrink-0 aspect-[3/4] sm:aspect-[2/3] bg-background-200 overflow-hidden">
          <img
            src={anime.cover}
            alt={`${anime.title}动漫封面`}
            title={`${anime.title}在线观看`}
            className="absolute inset-0 w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent"></div>

          <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs bg-background-50/90 text-foreground-800 font-medium">
            {anime.status}
          </span>

          <button
            type="button"
            onClick={handleFavorite}
            aria-label={favorited ? '取消收藏' : '收藏'}
            className={`absolute top-2 right-2 w-8 h-8 flex items-center justify-center rounded-full cursor-pointer transition-colors ${
              favorited
                ? 'bg-accent-500 text-background-50'
                : 'bg-background-50/85 text-foreground-700 hover:text-accent-500'
            }`}
          >
            <i className={favorited ? 'ri-heart-fill text-sm' : 'ri-heart-line text-sm'}></i>
          </button>

          {typeof rank === 'number' && (
            <span className="absolute bottom-2 left-2 w-7 h-7 flex items-center justify-center rounded-md bg-accent-500 text-background-50 font-heading font-bold text-sm">
              {rank}
            </span>
          )}

          <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded-full text-xs bg-secondary-500 text-secondary-950 font-semibold flex items-center gap-1">
            <i className="ri-star-fill text-[10px]"></i>
            {anime.rating.toFixed(1)}
          </span>

          <span className="hidden sm:flex absolute inset-0 items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-500/90 text-background-50">
              <i className="ri-play-fill text-2xl"></i>
            </span>
          </span>
        </div>

        <div className="p-3 sm:p-3 flex-1 min-w-0 flex flex-col justify-center sm:justify-start">
          <h3 className="font-heading font-semibold text-sm text-foreground-950 truncate group-hover:text-primary-600 transition-colors">
            {anime.title}
          </h3>
          <p className="mt-1 text-xs text-foreground-500 truncate">
            {anime.year} · {anime.region} · {anime.type}
          </p>
          <p className="mt-1 text-xs text-foreground-600 truncate">
            {anime.status === '已完结' ? `全 ${anime.episodes} 集` : `更新至第 ${anime.latestEpisode} 集`}
            <span className="mx-1 text-background-400">|</span>
            {anime.views.toFixed(1)}万播放
          </p>
          <p className="hidden sm:block mt-2 text-xs text-foreground-500 leading-relaxed line-clamp-2">
            {anime.description}
          </p>
          <div className="hidden sm:flex flex-wrap gap-1 mt-2">
            {anime.tags.slice(0, 3).map((tag) => (
              <span key={tag} className="px-2 py-0.5 rounded-full text-[11px] bg-secondary-100 text-secondary-900">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </Link>
  );
}